/**
 * Autofocus the entering dry-bulb input when a Performance subtab opens
 * (requested 2026-10-22, on the back of the psychrometric chart's
 * SYNCHRONIZE CONDITIONS button landing the user there — see
 * `psychrometric/PsychrometricChartPopup.tsx::openSectionConditions`).
 *
 * The same Performance group is rendered by three unrelated families, so
 * nothing here takes a ref. Two entry points, two DOM contracts:
 *
 *  - `useFocusEnteringTempOnOpen(open, toggleId, …)` — for a caller that
 *    knows the subtab's dom id: `FormSubtab` (every component form),
 *    `SectionForm`'s `isPlenum` branch, `UdaForm`. Contract: the toggle
 *    `<input id>` and the group it controls share a parent element.
 *  - `focusPerformanceEnteringTemp(root, …)` — for a caller that owns a form
 *    but not the id its subtabs are composed with (`useComponentForm`, which
 *    passes its own `li#forms_component_<row.id>` row shell, an id
 *    `TabComponents` owns). Contract: that shell contains exactly one
 *    `input[data-subtab="performance"]`, next to its group.
 *
 * ★ It focuses on an ARRIVAL, never on a plain re-render or re-mount. The
 * two arrivals are: the user opening the subtab themselves, and the chart's
 * jump (`performanceJump` in the board slice, passed in as `focusToken`).
 * EVERY jump is an arrival, including one that lands on a group already
 * open — reopening the chart and pressing the button again must focus again
 * (reported 2026-10-22). That is what `focusToken` is for, and why
 * `useComponentForm` calls `focusPerformanceEnteringTemp` directly.
 * A form that merely re-mounts with Performance already open — which happens
 * on every selection of a plenum that remembers it, and on every accessory
 * re-expand — must NOT take the keyboard: while the caret sits in an input,
 * `#forms-main-container` swallows keydown, so the canvas' arrow/Enter
 * navigation and the Delete-section shortcut stop working (review round 2).
 */

import { useEffect, useRef } from 'react';

/**
 * Entering dry-bulb inputs in the order to try them, and the default list.
 *
 * `_summer` is the one asked for. `_winter` is the fallback for a section
 * whose Season is Winter — `seasonShowFlags` renders no summer row at all
 * then, so there is nothing to focus under the first name.
 */
export const ENTERING_DB_NAMES = ['eadb_temp_summer', 'eadb_temp_winter'] as const;

/**
 * `UdaForm` only. Its "no season chosen" shape (the pre-split bare pair) has
 * neither per-season row, so the unsuffixed name is the only one there.
 *
 * Deliberately NOT in the default list: a coil's Performance subtab also
 * carries an editable unsuffixed `eadb_temp` (`CoilForm.tsx`), and a coil is
 * not what this feature is for — adding it there would make opening any
 * coil's Performance grab the keyboard.
 */
export const UDA_ENTERING_DB_NAMES = [...ENTERING_DB_NAMES, 'eadb_temp'];

/** Focus is only worth taking from the browser's default (body) or from the
 * subtab toggle the user just clicked. Anything else means the user is
 * already typing somewhere — a form that re-opens underneath them must not
 * yank the caret out. The toggles all carry `data-subtab`. */
function safeToSteal(): boolean {
  const active = document.activeElement as HTMLElement | null;
  if (!active) return true;
  if (!/^(INPUT|SELECT|TEXTAREA)$/.test(active.tagName)) return true;
  return active.getAttribute('data-subtab') != null;
}

function focusIn(group: Element | null | undefined, names: readonly string[]): void {
  if (!group) return;
  for (const name of names) {
    const input = group.querySelector<HTMLInputElement>(`input[name="${name}"]`);
    // `offsetParent` catches the rows the merged `[data-season]` CSS hides
    // without the JSX knowing; readOnly catches a form in select mode.
    if (input && !input.disabled && !input.readOnly && input.offsetParent !== null) {
      input.focus();
      return;
    }
  }
}

function focusEnteringTemp(toggleId: string, names: readonly string[]): void {
  focusIn(document.getElementById(toggleId)?.parentElement, names);
}

/**
 * The same focus, found from a form's ROOT rather than from the toggle id —
 * for a caller that owns the form but not the id its subtabs are rendered
 * with (`useComponentForm`, which reaches its own `li#forms_component_<id>`).
 * Scoped to the Performance group, so another subtab's entering dry bulb
 * can never be picked up by mistake.
 */
export function focusPerformanceEnteringTemp(
  root: Element | null | undefined,
  names: readonly string[] = ENTERING_DB_NAMES
): void {
  if (!root || !safeToSteal()) return;
  focusIn(root.querySelector('input[data-subtab="performance"]')?.parentElement, names);
}

/**
 * @param open        whether the Performance group is expanded
 * @param toggleId    dom id of its subtab checkbox
 * @param focusToken  a NEW non-null value demands focus even without a
 *                    closed→open transition — the chart jump landing on a
 *                    group that happens to be open already. Pass the jump's
 *                    nonce, and `undefined` once it has been consumed.
 * @param names       input names to try, in order (UdaForm widens this)
 */
export function useFocusEnteringTempOnOpen(
  open: boolean,
  toggleId: string,
  focusToken?: number,
  names: readonly string[] = ENTERING_DB_NAMES
): void {
  /** null until the first effect run — that run is a MOUNT, not an opening. */
  const wasOpen = useRef<boolean | null>(null);
  const lastToken = useRef<number | undefined>(undefined);
  useEffect(() => {
    const previous = wasOpen.current;
    wasOpen.current = open;
    const tokenFired = focusToken != null && focusToken !== lastToken.current;
    lastToken.current = focusToken;
    const opening = previous !== null && open && !previous;
    if (!open || (!opening && !tokenFired) || !safeToSteal()) return;
    focusEnteringTemp(toggleId, names);
    // `names` is a module constant at every call site — not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, toggleId, focusToken]);
}
